import { inject, Injectable } from '@angular/core';
import { catchError, from, lastValueFrom, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { AppContext } from '../../app/context';
import { ApiException, CoreSchemaTargetDto, DataRelationshipMappingDto, DataRelationshipMappingProductItemDto, DataRelationshipMappingUserApiService, SchemaTargetDto, SchemaType } from '../../services/api.services';
import { AppError, catchAppError, groupToMap } from '../../shared/utils';
import { SchemaUtils } from '../../shared/utils/schema-utils';
import { logger } from '../utils/logger';
import { VisualFieldTarget } from '../visuals/VisualFieldTarget';
import { VisualFieldTargetResolver } from '../visuals/VisualFieldTargetResolver';

const CACHE_TIME = 1000 * 60 * 2;

const LOGGER = logger('[DataRelationshipMappings]');

@Injectable({ providedIn: 'root' })
export class DataRelationshipMappings {
  readonly appContext = inject(AppContext);
  readonly dataRelationshipMappingUserApiService = inject(DataRelationshipMappingUserApiService);

  get() {
    return from(this._get());
  }

  async _get(): Promise<DataRelationshipMapping> {
    if (this._cachedPromise) {
      await this._cachedPromise;
      return this._cachedValue;
    }

    if (!this._cachedValue || Date.now() >= this._cachedTime) {
      this._cachedPromise = this._load();
      await this._cachedPromise;
    }

    return this._cachedValue;
  }

  private _cachedDto:     DataRelationshipMappingDto;
  private _cachedValue:   DataRelationshipMapping;
  private _cachedPromise: Promise<DataRelationshipMapping>;
  private _cachedTime = 0;

  private async _load(): Promise<DataRelationshipMapping> {
    try {
      let mappingDto: DataRelationshipMappingDto;

      try {
        mappingDto = await lastValueFrom(this.dataRelationshipMappingUserApiService.getOrganisationDataModelRelationshipMapping(
          this.appContext.organisationId(),
          '' + (this._cachedDto?.timestamp ?? 0)
        ));
      }
      catch (err) {
        if (err instanceof ApiException || err instanceof AppError) {
          if (err.status == 304) {
            LOGGER.info(err);
            mappingDto = this._cachedDto;
          }
          else {
            LOGGER.error(err);
            mappingDto = { products: [], timestamp: 0 };
          }
        }
      }

      let value = this._cachedValue;

      if (mappingDto !== this._cachedDto) {
        this._cachedDto   = mappingDto;
        this._cachedValue = value = new DataRelationshipMapping(mappingDto);
        this._cachedTime = Date.now() + CACHE_TIME;
      }

      return value;
    }
    finally {
      this._cachedPromise = null;
    }
  }

  clearCache() {
    this._cachedValue = null;
  }
}

export class DataRelationshipMappingItem {
  constructor(
    readonly dto: DataRelationshipMappingProductItemDto,
    readonly schema: SchemaTargetDto) { }

  readonly group = this.dto.groupName.toLowerCase();
  readonly field = this.dto.itemName.toLowerCase();
  readonly tag   = this.dto.tagName.toLowerCase();
}

export class DataRelationshipMapping {
  static readonly empty = new DataRelationshipMapping({
    timestamp: 0,
    products: [],
  });

  constructor(
    readonly dto: DataRelationshipMappingDto,
    readonly base?: DataRelationshipMapping,
    readonly extensions?: ReadonlyArray<DataRelationshipMapping>) { }

  private readonly _items = new Map<string, DataRelationshipMappingItem>(
    this.dto.products.flatMap(
      p => p.items.map(i => {
        let schema: CoreSchemaTargetDto = {
          type:    SchemaType.Core,
          name:    i.schemaName,
          product: p.productName,
        };

        return [
          itemKey(p.productName, i.schemaName, i.groupName, i.itemName),
          new DataRelationshipMappingItem(i, schema)];
      })
    )
  );

  readonly items: ReadonlyArray<DataRelationshipMappingItem> = [...this._items.values()];

  findItem(schema: SchemaTargetDto, group: string, field: string) {
    if (SchemaUtils.isCore(schema)) {
      let key = itemKey(schema.product, schema.name, group, field);
      let item = this._items.get(key);
      if (item)
        return item;
    }

    return this.fromBaseOrExtensions(x => x.findItem(schema, group, field));
  }

  
  private readonly _tags = groupToMap(this._items.values(), x => x.tag);

  readonly tags: ReadonlySet<string> = new Set<string>(this.items.map(x => x.tag));

  getTaggedItems(tag: string): ReadonlyArray<DataRelationshipMappingItem> {
    return this._tags.get(tag) ?? this.fromBaseOrExtensions(x => x.getTaggedItems(tag));
  }


  getMapping(
    targetResolver: VisualFieldTargetResolver,
    target: VisualFieldTarget,
    options?: { includeSelf?: boolean; targetSchema?: SchemaTargetDto }): ReadonlyArray<VisualFieldTarget> {
    let includeSelf = !!options?.includeSelf;
    let targetSchema = options?.targetSchema;

    let result: VisualFieldTarget[] = [];

    if (includeSelf)
      result.push(target);

    let targetDto = target.schemaFieldTargetDto;

    let item = this.findItem(
      target.schema.getSchemaTargetDto(),
      target.itemGroupName,
      target.shortTitle);

    if (item) {
      let taggedItems = this.getTaggedItems(item.tag);

      for (let i of taggedItems) {
        if (i === item)
          continue;

        let mapped = targetResolver.find({
          schema: i.schema,
          group: i.group,
          field: i.field,
          childField: targetDto.childField,
          aggregation: targetDto.aggregation,
          visualId: targetDto.visualId,
        });

        if (!mapped) {
          LOGGER.warn(`Could not resolve mapping from '${target.referenceString}' to '[${i.schema.name}].[${i.group}].[${i.field}]'`);
          continue;
        }

        if (targetSchema && !SchemaUtils.same(i.schema, targetSchema)) {
          continue;
        }

        result.push(mapped);
      }
    }

    if (result.length > 0)
      return result;

    return this.fromBaseOrExtensions(x => x.getMapping(targetResolver, target, options), []);
  }

  private fromBaseOrExtensions<R>(callback: ((x: DataRelationshipMapping) => R), defaultValue?: R): R {
    if (this.base) {
      let result = callback(this.base);
      if (result)
        return result;
    }

    if (this.extensions) {
      for (let extension of this.extensions) {
        let result = callback(extension);
        if (result)
          return result;
      }
    }

    return defaultValue;
  }
}

function itemKey(p: string, s: string, g: string, i: string) {
  return `[${p.toLowerCase()}].[${s.toLowerCase()}].[${g.toLowerCase()}].[${i.toLowerCase()}]`;
}
